import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input } from '@arco-design/web-react';
import isEmpty from 'lodash/isEmpty';

import { genId } from '@/utils';

import { VolumeEmptyActionPanel } from '../../components/VolumeEmptyActionPanel';
import { useDiskPricingSync } from '../../components/diskPricingSync';
import { buildStorageClassLabelMap, useStorageClassFormValidation } from '../../components/storageClassField';
import { VendorDevicePersistFields } from '../../components/vendorDevice';
import type { CapabilityControlProps } from '../../core/types';
import { isActionVisible, isFieldVisible, resolveActionMode, resolveFieldMode } from '../../platform/capability-registry/resolveFieldMode';
import { shouldHideEmptyListBlock } from '../../platform/capability-registry/shouldHideEmptyListBlock';
import { mergeOptionMetaForValues } from '../../platform/option-meta';
import { AddClaimTemplateModal } from './components/AddClaimTemplateModal';
import { AddVolumeModal } from './components/AddVolumeModal';
import { StorageItems } from './components/StorageItems';
import { CONTAINER_STORAGE_ACTION_KEYS, CONTAINER_STORAGE_FIELD_KEYS } from './fieldKeys';
import type { ContainerStorageBlockProps, ContainerStoragePaths } from './types';
import { claimTemplateIndexesMissingStorageClass, normalizeContainerVolumeList } from './utils';
import { type VolumeItem, isVolumeClaimTemplateItem, isVolumeStorageItem } from './volumeTypes';

const FormItem = Form.Item;

function isReadonlyBlock(control: CapabilityControlProps): boolean {
  return control.blockMode === 'preview';
}

function isReadonlyVolumeList(control: CapabilityControlProps): boolean {
  const mode = resolveFieldMode(CONTAINER_STORAGE_FIELD_KEYS.list, control);
  return mode === 'readonly' || isReadonlyBlock(control);
}

function isActionEnabled(actionKey: string, control: CapabilityControlProps): boolean {
  return resolveActionMode(actionKey, control) === 'enabled';
}

function readVolumeList(form: ContainerStorageBlockProps['form'], path: string): VolumeItem[] {
  return normalizeContainerVolumeList(form.getFieldValue(path));
}

function writeVolumeList(form: ContainerStorageBlockProps['form'], path: string, list: VolumeItem[]) {
  form.setFieldValue(path, list);
}

/** 容器实例存储配置：声明模板 + 挂载卷（受控 field/action + 共享列表 UI） */
export function ContainerStorageBlock(props: ContainerStorageBlockProps) {
  const { form, paths: rawPaths, options, linkageProps, ...control } = props;
  const paths = rawPaths as ContainerStoragePaths;
  const { t } = useTranslation();
  const { pricingActive, syncListPricing } = useDiskPricingSync(options);

  const isEdit = options?.isEdit === true;
  const readonly = isReadonlyVolumeList(control);

  const namespace = linkageProps?.namespace ?? options?.namespace;
  const containerName = linkageProps?.containerName ?? options?.containerName;

  const volumesRaw = Form.useWatch(paths.volumes, form);
  const optionMeta = Form.useWatch('optionMeta', form);
  const volumes = useMemo(() => normalizeContainerVolumeList(volumesRaw), [volumesRaw]);

  const { rules: storageClassRules, missingIndexes: storageClassErrorIndexes } = useStorageClassFormValidation(
    volumesRaw,
    pricingActive,
    claimTemplateIndexesMissingStorageClass,
    t('common:please.select.storage.class'),
  );

  const storageClassLabelMap = useMemo(() => {
    const formValues = form.getFieldsValue() as Record<string, unknown>;
    return buildStorageClassLabelMap(volumes, { ...formValues, optionMeta }, paths.volumes);
  }, [form, optionMeta, paths.volumes, volumes]);

  const [claimTemplateModalVisible, setClaimTemplateModalVisible] = useState(false);
  const [volumeModalVisible, setVolumeModalVisible] = useState(false);
  const [editVolume, setEditVolume] = useState<VolumeItem | null>(null);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const clearEditState = useCallback(() => {
    setEditVolume(null);
    setEditIndex(null);
  }, []);

  const handleEdit = useCallback((item: VolumeItem, index: number) => {
    setEditVolume(item);
    setEditIndex(index);
    if (isVolumeStorageItem(item)) {
      setVolumeModalVisible(true);
    }
    if (isVolumeClaimTemplateItem(item)) {
      setClaimTemplateModalVisible(true);
    }
  }, []);

  const handleDelete = useCallback(
    (item: VolumeItem) => {
      const nextList = readVolumeList(form, paths.volumes).filter((v) => v.volumeName !== item.volumeName);
      writeVolumeList(form, paths.volumes, nextList);
      syncListPricing(form, paths.volumes, nextList, (row) => isVolumeClaimTemplateItem(row));
    },
    [form, paths.volumes, syncListPricing],
  );

  const handleCancel = useCallback(() => {
    clearEditState();
    setVolumeModalVisible(false);
    setClaimTemplateModalVisible(false);
  }, [clearEditState]);

  const handleOk = useCallback(
    (values: VolumeItem, modalFormValues?: Record<string, unknown>) => {
      const list = readVolumeList(form, paths.volumes);
      let nextList: VolumeItem[];
      if (isEmpty(editVolume)) {
        const volumeName = values.volumeName || genId({ prefix: 'volume-' });
        nextList = [...list, { ...values, volumeName }];
      } else if (editIndex != null) {
        const current = list[editIndex];
        const merged =
          isVolumeClaimTemplateItem(values) && isVolumeClaimTemplateItem(current)
            ? { ...values, pricing: values.pricing ?? current.pricing }
            : values;
        nextList = list.map((item, index) => (index === editIndex ? merged : item));
      } else {
        nextList = list;
      }
      writeVolumeList(form, paths.volumes, nextList);
      if (modalFormValues) {
        if (isVolumeClaimTemplateItem(values)) {
          mergeOptionMetaForValues(form, modalFormValues, [values.storageClass, values.reclaimPolicy]);
        } else if (isVolumeStorageItem(values)) {
          mergeOptionMetaForValues(form, modalFormValues, [values.selected, values.reclaimPolicy]);
        }
      }
      if (isVolumeStorageItem(values)) {
        options?.onResourceWatchSnapshot?.(form);
      } else {
        syncListPricing(form, paths.volumes, nextList, (row) => isVolumeClaimTemplateItem(row));
      }
      handleCancel();
    },
    [editIndex, editVolume, form, handleCancel, options, paths.volumes, syncListPricing],
  );

  const canAddClaimTemplate = isActionVisible(CONTAINER_STORAGE_ACTION_KEYS.addClaimTemplate, control) && !readonly && !isEdit;
  const canAddMountVolume = isActionVisible(CONTAINER_STORAGE_ACTION_KEYS.addMountVolume, control) && !readonly;
  const canEditVolume = isActionVisible(CONTAINER_STORAGE_ACTION_KEYS.edit, control) && !readonly;
  const canDeleteVolume = isActionVisible(CONTAINER_STORAGE_ACTION_KEYS.remove, control) && !readonly;

  if (!isFieldVisible(CONTAINER_STORAGE_FIELD_KEYS.list, control)) {
    return null;
  }

  if (shouldHideEmptyListBlock(control, CONTAINER_STORAGE_FIELD_KEYS.list, volumes.length === 0)) {
    return null;
  }

  const emptyActions = [
    ...(canAddClaimTemplate && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.addClaimTemplate, control)
      ? [
          {
            key: 'addClaimTemplate',
            title: t('data-center:add.shared.volume.claim.template'),
            description: t('data-center:add.shared.volume.claim.template.to.mount.a.shared.volume.for.container.instances'),
            onClick: () => {
              clearEditState();
              setClaimTemplateModalVisible(true);
            },
          },
        ]
      : []),
    ...(canAddMountVolume && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.addMountVolume, control)
      ? [
          {
            key: 'addMountVolume',
            title: t('common:mount.volume'),
            description: t('data-center:mount.shared.volume.for.container.instances'),
            onClick: () => {
              clearEditState();
              setVolumeModalVisible(true);
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <FormItem field={paths.volumes} hidden rules={storageClassRules} style={{ display: 'none', margin: 0 }}>
        <Input />
      </FormItem>
      {pricingActive
        ? volumes.map((volume, index) =>
            isVolumeClaimTemplateItem(volume) ? (
              <VendorDevicePersistFields
                key={`${volume.volumeName ?? 'claim'}-${index}-pricing`}
                fieldPrefix={`${paths.volumes}.${index}.pricing`}
              />
            ) : null,
          )
        : null}

      <StorageItems
        items={volumes}
        mode={readonly ? 'readonly' : undefined}
        onEdit={canEditVolume && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.edit, control) ? handleEdit : undefined}
        onDelete={canDeleteVolume && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.remove, control) ? handleDelete : undefined}
        storageClassErrorIndexes={storageClassErrorIndexes}
        storageClassLabelMap={storageClassLabelMap}
        canEdit={canEditVolume && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.edit, control)}
        canDelete={canDeleteVolume && isActionEnabled(CONTAINER_STORAGE_ACTION_KEYS.remove, control)}
        namespace={namespace}
        showMountedVolumeCapacity={options?.showMountedVolumeCapacity}
      />

      {!readonly ? <VolumeEmptyActionPanel items={emptyActions} /> : null}

      {canAddClaimTemplate || canEditVolume ? (
        <AddClaimTemplateModal
          open={claimTemplateModalVisible}
          onClose={handleCancel}
          onOk={handleOk}
          data={editVolume ?? undefined}
          containerName={containerName}
          from="container-create"
          billModelId={options?.billModelId}
          deploymentTypeId={options?.deploymentTypeId}
          resourceCode={options?.resourceCode}
          resourceEditMode={options?.resourceEditMode}
        />
      ) : null}

      {canAddMountVolume || canEditVolume ? (
        <AddVolumeModal
          namespace={namespace ?? ''}
          open={volumeModalVisible}
          onClose={handleCancel}
          onOk={handleOk}
          data={editVolume ?? undefined}
          containerName={containerName}
          from="container-create"
          showMountedVolumeCapacity={options?.showMountedVolumeCapacity}
          resourceEditMode={options?.resourceEditMode}
        />
      ) : null}
    </>
  );
}
